import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Ban,
  BedDouble,
  FileText,
  Plane,
  MessageSquareText,
  Send,
  Trash2,
  Receipt,
  Plus,
  Download,
  // ShieldAlert,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import { Label } from "ui/label";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "ui/alert-dialog";
import { getLawazimList } from "services/lawazimConfigService";
import { LawazimConfig, RoomTypeCharges } from "types/lawazimConfig";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  RoomAllocationApplicant,
  cancelRoomAllocation,
  getTourCancellationTiers,
  TourCancellationTier,
  cancelApplicant,
  deleteShortlistedApplicant,
} from "services/tourApprovalService";
import {
  getBookingCharges,
  BookingCharge,
  cancelTour,
} from "services/tourService";
import {
  getBookingReceiptSummaryByTourCode,
  getBookingReceiptsByTourCode,
  BookingReceiptApi,
  downloadBookingReceiptPdf,
} from "services/bookingReceiptService";
import { useScreenPermission } from "hooks/useScreenPermission";

interface OverviewRoom {
  id: string;
  number: string;
  type: string;
  occupancy: number;
  maxOccupancy: number;
  requestedNumber: number;
  applicants: RoomAllocationApplicant[];
  venue: string;
  city: string;
  propertyPublicId: string;
  propertyRoomTypePublicId: string;
  roomSelectionPublicId: string;
  propertyRoomTypeId: number;
  propertyId: number;
  venueId: number;
  roomAllocationPublicId?: string;
  status: string;
}

interface OverviewVenue {
  venueId: number | null;
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
}

interface OverviewApplicant {
  id: string;
  publicId: string;
  name: string;
  itsNumber: string;
  gender: string;
}

interface OverviewProps {
  tourName: string;
  tourPublicId: string;
  tourCode: string;
  selectedApplicants: string[];
  shortlistedApplicants: OverviewApplicant[];
  cancelPaxApplicants: RoomAllocationApplicant[];
  faizAccommodation: "yes" | "no";
  isFaizTour: boolean;
  venues: OverviewVenue[];
  excursionsByDay: Record<number, unknown[]>;
  mockServices: unknown[];
  arrivalPort: string;
  departurePort: string;
  arrivalFlightNumber: string;
  arrivalDate: string;
  arrivalTime: string;
  departureFlightNumber: string;
  departureDate: string;
  departureTime: string;
  portMasterData: { id: string; name: string }[];
  isPastCutoff: boolean;
  rooms: OverviewRoom[];
  isReadOnly: boolean;
  isExternalTour: boolean;
  isMiqaatTour: boolean;
  setRooms: React.Dispatch<React.SetStateAction<OverviewRoom[]>>;
  cancelledRoomIds: string[];
  setCancelledRoomIds: React.Dispatch<React.SetStateAction<string[]>>;
  isTourCancelled: boolean;
  setIsTourCancelled: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onRefresh: () => Promise<void>;
}

type CancellationPreview = {
  room: OverviewRoom;
  lawazimName: string;
  rule: string;
  calculation: string;
  amount: number;
  configured: boolean;
};

type SupportMessage = {
  id: string;
  sender: "admin" | "tour_operator";
  senderName: string;
  message: string;
  timestamp: string;
};

const formatAmount = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const textEquals = (left?: string | number, right?: string | number) =>
  String(left || "")
    .trim()
    .toLowerCase() ===
  String(right || "")
    .trim()
    .toLowerCase();

const getNights = (fromDate: string, toDate: string) => {
  const difference = new Date(toDate).getTime() - new Date(fromDate).getTime();
  return Math.max(1, Math.ceil(difference / (1000 * 60 * 60 * 24)) || 1);
};

const findRoomLawazim = (room: OverviewRoom, configs: LawazimConfig[]) => {
  for (const lawazim of configs) {
    const charge = (lawazim.roomTypeCharges || []).find(
      (item) =>
        (textEquals(item.roomTypeName, room.type) ||
          textEquals(item.roomTypeCode, room.type) ||
          textEquals(item.roomTypeId, room.type)) &&
        (!item.propertyName ||
          textEquals(item.propertyName, room.venue) ||
          textEquals(item.propertyName, room.city)),
    );
    if (charge) return { lawazim, charge };
  }

  for (const lawazim of configs) {
    const charge = (lawazim.roomTypeCharges || []).find(
      (item) =>
        textEquals(item.roomTypeName, room.type) ||
        textEquals(item.roomTypeCode, room.type) ||
        textEquals(item.roomTypeId, room.type),
    );
    if (charge) return { lawazim, charge };
  }

  return {} as { lawazim?: LawazimConfig; charge?: RoomTypeCharges };
};

export default function Overview({
  tourName,
  tourPublicId,
  tourCode,
  selectedApplicants,
  shortlistedApplicants,
  cancelPaxApplicants,
  faizAccommodation,
  isFaizTour,
  venues,
  excursionsByDay,
  mockServices,
  arrivalPort,
  departurePort,
  arrivalFlightNumber,
  arrivalDate,
  arrivalTime,
  departureFlightNumber,
  departureDate,
  departureTime,
  portMasterData,
  isPastCutoff,
  rooms,
  setRooms,
  cancelledRoomIds,
  setCancelledRoomIds,
  isTourCancelled,
  setIsTourCancelled,
  setLoading,
  onRefresh,
  isReadOnly,
  isExternalTour,
  isMiqaatTour,
}: OverviewProps) {
  const navigate = useNavigate();
  const [bookingReceiptSummary, setBookingReceiptSummary] = useState({
    totalAmount: 0,
    amountReceived: 0,
    balanceDue: 0,
  });

  const [, setBookingReceiptSummaryLoading] = useState(false);

  const [bookingReceipts, setBookingReceipts] = useState<BookingReceiptApi[]>(
    [],
  );

  const [bookingReceiptsLoading, setBookingReceiptsLoading] = useState(false);
  const formatReceiptAmount = (amount: number) =>
    amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handleDownloadBookingReceipt = async (receipt: BookingReceiptApi) => {
    try {
      const pdfBlob = await downloadBookingReceiptPdf(receipt.publicId);

      const url = window.URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${receipt.receiptNumber.replace(
        /[^a-zA-Z0-9_-]/g,
        "_",
      )}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      toast.success(`Receipt ${receipt.receiptNumber} downloaded successfully`);
    } catch (error) {
      console.error("Failed to download booking receipt PDF:", error);

      toast.error("Failed to download booking receipt.");
    }
  };
  const [lawazimConfigs, setLawazimConfigs] = useState<LawazimConfig[]>([]);
  const [, setLoadingLawazim] = useState(true);
  const [pendingCancellation, setPendingCancellation] = useState<
    { type: "room"; roomId: string } | { type: "tour" } | { type: "pax" } | null
  >(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<OverviewRoom | null>(null);
  const [showCancelPaxDialog, setShowCancelPaxDialog] = useState(false);

  const { canAdd } = useScreenPermission();

  const hasBookingReceiptMenu = useMemo(() => {
    try {
      const storedPermissions = localStorage.getItem("permissions");

      if (!storedPermissions) {
        return false;
      }

      const permissions = JSON.parse(storedPermissions);

      return permissions.some(
        (permission: {
          screenCode?: string;
          screenName?: string;
          canAdd?: boolean;
        }) =>
          (permission.screenCode === "FINA-BOOKING-REP" ||
            permission.screenName === "Booking Receipts") &&
          permission.canAdd === true,
      );
    } catch {
      return false;
    }
  }, []);

  const canCreateBookingReceipt = canAdd && hasBookingReceiptMenu;

  const [applicantToDelete, setApplicantToDelete] = useState<
    (OverviewApplicant & { isShortlisted: boolean }) | null
  >(null);
  const [showPaxDeleteDialog, setShowPaxDeleteDialog] = useState(false);
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportConversation, setSupportConversation] = useState<
    SupportMessage[]
  >([
    {
      id: "admin-welcome",
      sender: "admin" as const,
      senderName: "Admin",
      message: "Hello. Please share the support required for this tour.",
      timestamp: "Admin support",
    },
  ]);

  const [, setLoadingCharges] = useState(false);
  const [apiCharges, setApiCharges] = useState<
    {
      roomId: string;
      propertyPublicId: string;
      propertyRoomTypePublicId: string;
      charge: BookingCharge;
    }[]
  >([]);

  const [cancellationTiers, setCancellationTiers] = useState<
    TourCancellationTier[]
  >([]);
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [cancellingTour, setCancellingTour] = useState(false);
  const isDisabled = isReadOnly || isTourCancelled;

  const sendSupportMessage = () => {
    const message = supportMessage.trim();
    if (!message) return;
    setSupportConversation((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        sender: "tour_operator" as const,
        senderName: "Tour Operator",
        message,
        timestamp: new Date().toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
    setSupportMessage("");
  };

  const uniqueCancelPaxApplicants = useMemo(() => {
    const applicantsMap = new Map<
      string,
      {
        visaApplicationId: string;
        publicId: string;
        applicantName: string;
        itsNo: string;
        gender: string;
        isShortlisted: boolean;
      }
    >();

    cancelPaxApplicants.forEach((applicant) => {
      const key = applicant.visaApplicationId.toString();

      applicantsMap.set(key, {
        visaApplicationId: key,
        publicId: applicant.publicId,
        applicantName: applicant.applicantName,
        itsNo: applicant.itsNo,
        gender: "",
        isShortlisted: false,
      });
    });

    shortlistedApplicants.forEach((applicant) => {
      const key = applicant.id.toString();

      if (!applicantsMap.has(key)) {
        applicantsMap.set(key, {
          visaApplicationId: key,
          publicId: applicant.publicId,
          applicantName: applicant.name,
          itsNo: applicant.itsNumber,
          gender: applicant.gender,
          isShortlisted: true,
        });
      }
    });

    return Array.from(applicantsMap.values());
  }, [cancelPaxApplicants, shortlistedApplicants]);

  useEffect(() => {
    if (!tourCode) {
      setBookingReceiptSummary({
        totalAmount: 0,
        amountReceived: 0,
        balanceDue: 0,
      });
      return;
    }

    const loadBookingReceiptSummary = async () => {
      try {
        setBookingReceiptSummaryLoading(true);

        const response = await getBookingReceiptSummaryByTourCode(tourCode);

        if (!response.success) {
          toast.error(
            response.message || "Failed to load booking receipt summary.",
          );
          return;
        }

        setBookingReceiptSummary({
          totalAmount: response.data.totalAmount ?? 0,
          amountReceived: response.data.amountReceived ?? 0,
          balanceDue: response.data.balanceDue ?? 0,
        });
      } catch (error) {
        console.error("Failed to load booking receipt summary:", error);

        setBookingReceiptSummary({
          totalAmount: 0,
          amountReceived: 0,
          balanceDue: 0,
        });

        toast.error("Failed to load booking receipt summary.");
      } finally {
        setBookingReceiptSummaryLoading(false);
      }
    };

    loadBookingReceiptSummary();
  }, [tourCode]);

  useEffect(() => {
    if (!tourCode) {
      setBookingReceipts([]);
      return;
    }

    const loadBookingReceipts = async () => {
      try {
        setBookingReceiptsLoading(true);

        const response = await getBookingReceiptsByTourCode(tourCode);

        if (!response.success) {
          toast.error(response.message || "Failed to load booking receipts.");
          setBookingReceipts([]);
          return;
        }

        setBookingReceipts(response.data ?? []);
      } catch (error) {
        console.error("Failed to load booking receipts:", error);

        setBookingReceipts([]);
        toast.error("Failed to load booking receipts.");
      } finally {
        setBookingReceiptsLoading(false);
      }
    };

    loadBookingReceipts();
  }, [tourCode]);

  useEffect(() => {
    let active = true;
    getLawazimList({ pageNumber: 1, pageSize: 500 })
      .then((data) => {
        if (active) {
          setLawazimConfigs(
            (data?.items || []).filter((item: LawazimConfig) => item.isActive),
          );
        }
      })
      .catch(() => {
        if (active) toast.error("Failed to load Lawazim cancellation charges");
      })
      .finally(() => {
        if (active) setLoadingLawazim(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const loadCharges = async () => {
      try {
        setLoadingCharges(true);

        const selectedRooms = rooms.filter(
          (room) => room.propertyId && room.propertyRoomTypeId,
        );

        const chargesResponse = await getBookingCharges({
          chargeCalculationMode: 2,
          cancellationScope: 0,
          tourPublicId,
        });

        const cancellationResponse =
          await getTourCancellationTiers(tourPublicId);

        setCancellationTiers(
          cancellationResponse.data?.cancellationTiers ?? [],
        );

        const mapped = chargesResponse.charges.map((charge) => {
          const room = selectedRooms.find(
            (r) =>
              r.propertyPublicId === charge.propertyPublicId &&
              r.propertyRoomTypePublicId === charge.propertyRoomTypePublicId,
          );

          return {
            roomId: room?.id ?? "",
            propertyPublicId: charge.propertyPublicId,
            propertyRoomTypePublicId: charge.propertyRoomTypePublicId,
            charge,
          };
        });

        setApiCharges(mapped);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingCharges(false);
      }
    };

    loadCharges();
  }, [tourPublicId, rooms]);

  const roomCharges = useMemo(() => {
    return rooms.map((room) => ({
      room,
      charge: apiCharges.find((c) => c.roomId === room.id)?.charge,
    }));
  }, [rooms, apiCharges]);

  const cancellationPreviews = useMemo<CancellationPreview[]>(() => {
    const checkInDate =
      venues
        .map((venue) => venue.fromDate)
        .filter(Boolean)
        .sort()[0] || arrivalDate;
    const checkOutDate =
      venues
        .map((venue) => venue.toDate)
        .filter(Boolean)
        .sort()
        .at(-1) || departureDate;
    const nights = getNights(checkInDate, checkOutDate);
    const hoursBeforeCheckIn = Math.max(
      0,
      (new Date(checkInDate).getTime() - Date.now()) / (1000 * 60 * 60),
    );
    const daysBeforeCheckIn = Math.floor(hoursBeforeCheckIn / 24);

    return rooms.map((room) => {
      const { lawazim, charge } = findRoomLawazim(room, lawazimConfigs);
      const basePrice = Number(charge?.basePrice || 0);
      const fixedRate = String(lawazim?.chargeType || "")
        .toLowerCase()
        .includes("fixed");
      const roomCharge = basePrice * (fixedRate ? 1 : nights);
      const policy = lawazim?.cancellationPolicyConfig;
      const tier = [...(policy?.tiers || [])]
        .filter((item) => daysBeforeCheckIn >= Number(item.daysBeforeCheckIn))
        .sort((a, b) => b.daysBeforeCheckIn - a.daysBeforeCheckIn)[0];

      if (policy && tier) {
        const calculated =
          roomCharge * (Number(tier.penaltyPercentage || 0) / 100);
        const amount = Math.max(calculated, Number(policy.minimumFee || 0));
        return {
          room,
          lawazimName: lawazim?.lawazimName || "Lawazim",
          rule: `${tier.daysBeforeCheckIn}+ days before check-in`,
          calculation: `${tier.penaltyPercentage}% of ${formatAmount(roomCharge)}`,
          amount,
          configured: true,
        };
      }

      const slab =
        (lawazim?.cancellationPolicies || []).find((item) => {
          const afterStart =
            item.fromHours === undefined ||
            hoursBeforeCheckIn >= item.fromHours;
          const beforeEnd =
            item.toHours === undefined || hoursBeforeCheckIn <= item.toHours;
          return afterStart && beforeEnd;
        }) || lawazim?.cancellationPolicies?.[0];

      if (slab) {
        let amount = Number(slab.chargeValue || 0);
        let calculation = formatAmount(amount);
        if (slab.chargeType === "percentage") {
          amount = roomCharge * (Number(slab.chargeValue || 0) / 100);
          calculation = `${slab.chargeValue}% of ${formatAmount(roomCharge)}`;
        } else if (slab.chargeType === "night-charge") {
          const nightCount = Math.max(1, Number(slab.nightCount || 1));
          amount = basePrice * nightCount;
          calculation = `${nightCount} night(s) x ${formatAmount(basePrice)}`;
        }
        return {
          room,
          lawazimName: lawazim?.lawazimName || "Lawazim",
          rule: slab.slabName || "Cancellation policy",
          calculation,
          amount,
          configured: true,
        };
      }

      return {
        room,
        lawazimName: lawazim?.lawazimName || "Not configured",
        rule: "No applicable cancellation policy",
        calculation: "Charge unavailable",
        amount: 0,
        configured: false,
      };
    });
  }, [arrivalDate, departureDate, lawazimConfigs, rooms, venues]);

  const pendingPreviews =
    pendingCancellation?.type === "room"
      ? cancellationPreviews.filter(
          (item) => item.room.id === pendingCancellation.roomId,
        )
      : cancellationPreviews.filter(
          (item) => !cancelledRoomIds.includes(item.room.id),
        );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pendingCharge = pendingPreviews.reduce((total, item) => {
    const charge = roomCharges.find((r) => r.room.id === item.room.id)?.charge;

    return total + (charge?.totalCharge ?? 0);
  }, 0);

  const formatDate = (date: string) => {
    if (!date) return "";

    const d = new Date(date);

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return `${d.getDate().toString().padStart(2, "0")} ${
      months[d.getMonth()]
    } ${d.getFullYear()}`;
  };

  const cancelPax = async () => {
    if (!applicantToDelete) return;
    try {
      setLoading(true);
      let response;
      if (applicantToDelete.isShortlisted) {
        response = await deleteShortlistedApplicant(applicantToDelete.publicId);
      } else {
        response = await cancelApplicant(
          tourPublicId,
          applicantToDelete.publicId,
        );
      }
      if (!response.success) {
        toast.error(response.message || "Failed to delete applicant.");
        return;
      }
      setShowPaxDeleteDialog(false);
      setApplicantToDelete(null);
      setShowCancelPaxDialog(false);
      await onRefresh();
      toast.success(response.message || "Applicant deleted successfully.");
    } catch (error: unknown) {
      console.error("Failed to delete applicant:", error);
      toast.error("Failed to delete applicant.");
    } finally {
      setLoading(false);
    }
  };

  const confirmCancellation = async () => {
    if (!pendingCancellation) return;
    if (pendingCancellation.type === "room") {
      const roomId = pendingCancellation.roomId;
      setCancelledRoomIds((current) =>
        current.includes(roomId) ? current : [...current, roomId],
      );
      setRooms((current) =>
        current.map((room) =>
          room.id === roomId ? { ...room, applicants: [], occupancy: 0 } : room,
        ),
      );
      toast.success("Room cancellation confirmed");
    } else {
      try {
        setCancellingTour(true);

        const response = await cancelTour(tourPublicId);

        if (!response.success) {
          toast.error(response.message || "Failed to cancel tour.");
          return;
        }

        setIsTourCancelled(true);

        setCancelledRoomIds(rooms.map((room) => room.id));

        setRooms((current) =>
          current.map((room) => ({
            ...room,
            applicants: [],
            occupancy: 0,
          })),
        );

        setPendingCancellation(null);

        toast.success(response.message);
        setTimeout(() => {
          navigate("/tours?bookingFilter=all");
        }, 1000);
      } catch (error) {
        console.error(error);
        toast.error("Failed to cancel tour.");
      } finally {
        setCancellingTour(false);
        setPendingCancellation(null);
      }
    }
  };

  const cancelRoom = async (room: OverviewRoom) => {
    try {
      if (!room.roomAllocationPublicId) {
        toast.error("Room Allocation Id not found.");
        return;
      }

      const response = await cancelRoomAllocation(
        tourPublicId,
        room.roomAllocationPublicId,
      );

      const updatedRoom = response.data.roomAllocations.find(
        (r) => r.publicId === room.roomAllocationPublicId,
      );

      if (!response.success) {
        toast.error(response.message);
        return;
      }

      setCancelledRoomIds((current) =>
        current.includes(room.id) ? current : [...current, room.id],
      );

      setRooms((current) =>
        current.map((r) =>
          r.id === room.id
            ? {
                ...r,
                applicants: [],
                occupancy: 0,
                roomAllocationPublicId: undefined,
                status: updatedRoom?.status ?? "Cancelled",
              }
            : r,
        ),
      );

      toast.success(response.message);

      const remainingRooms = response.data.roomAllocations;

      if (remainingRooms.length === 0) {
        setPendingCancellation(null);
      }
    } catch (error: unknown) {
      toast.error(
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
          (error as { message?: string }).message ||
          "Failed to cancel room.",
      );
    }
  };

  const confirmRoomDelete = async () => {
    if (!roomToDelete) return;

    await cancelRoom(roomToDelete);

    setShowDeleteDialog(false);
    setRoomToDelete(null);
  };

  function GenderAvatar({
    gender,
    name,
    size = "md",
  }: {
    gender: string;
    name: string;
    size?: "sm" | "md" | "lg";
  }) {
    const isFemale = gender.toLowerCase() === "female";
    const sizeClass =
      size === "sm"
        ? "h-7 w-7 text-sm"
        : size === "lg"
          ? "h-10 w-10 text-xl"
          : "h-8 w-8 text-base";
    return (
      <span
        title={`${name} · ${gender}`}
        aria-label={`${name}, ${gender}`}
        className={`flex shrink-0 items-center justify-center rounded-full border-2 border-white shadow-sm ${sizeClass} ${
          isFemale ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"
        }`}
      >
        {isFemale ? "👩" : "👨"}
      </span>
    );
  }

  return (
    <div className="space-y-2 overflow-x-hidden">
      {/* Important Notice */}
      {isPastCutoff && (
        <Card className="border-red-200 bg-red-50 shadow-none">
          <CardContent className="p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-red-800">
                  <span className="font-semibold">Cutoff date passed.</span>{" "}
                  Registration and payment changes require support.
                </p>
              </div>
              <Button
                onClick={() => setSupportChatOpen(true)}
                className="h-8 w-full bg-red-600 px-3 text-xs text-white hover:bg-red-700 sm:w-auto"
                size="sm"
              >
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {/* Travel Dates */}
        <Card
          className="border-green-200 shadow-none"
          style={{ marginTop: "0px", gap: "0px" }}
        >
          <CardHeader style={{ padding: "12px" }}>
            <CardTitle className="text-sm flex items-center gap-2">
              <Plane className="h-4 w-4 text-green-600" />
              Travel Dates
            </CardTitle>
          </CardHeader>
          <CardContent
            className="p-2 pt-2"
            style={{ padding: "0px 12px 0px 12px" }}
          >
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-md bg-green-50/60 px-3 py-2">
                <Label className="text-xs text-gray-600">Arrival</Label>
                <p className="text-sm font-medium text-gray-900">
                  {portMasterData.find((p) => p.id === arrivalPort)?.name ||
                    arrivalPort}
                </p>
                <p className="mt-0.5 truncate text-xs text-gray-600">
                  {arrivalFlightNumber} • {formatDate(arrivalDate)} at{" "}
                  {arrivalTime}
                </p>
              </div>
              <div className="rounded-md bg-green-50/60 px-3 py-2">
                <Label className="text-xs text-gray-600">Departure</Label>
                <p className="text-sm font-medium text-gray-900">
                  {portMasterData.find((p) => p.id === departurePort)?.name ||
                    departurePort}
                </p>
                <p className="mt-0.5 truncate text-xs text-gray-600">
                  {departureFlightNumber} • {formatDate(departureDate)} at{" "}
                  {departureTime}
                </p>
              </div>
            </div>
          </CardContent>
          {/* Cancellation Management */}
          {!isExternalTour && (
            <CardContent style={{ padding: "12px 12px 0px 12px" }}>
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-4 mb-4">
                <Button
                  type="button"
                  className="h-8 w-full px-3 text-xs text-white hover:bg-[#4a8ac2] sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return;
                    setShowCancellationPolicy(true);
                  }}
                >
                  Cancellation Policy
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  className="h-8 w-full px-3 text-xs sm:w-auto"
                  disabled={
                    isDisabled || uniqueCancelPaxApplicants.length === 0
                  }
                  onClick={() => {
                    if (isDisabled) return;
                    setShowCancelPaxDialog(true);
                  }}
                >
                  <Ban className="h-4 w-4" />
                  Cancel Pax
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  className="h-8 w-full px-3 text-xs sm:w-auto"
                  disabled={rooms.length === 0 || isDisabled}
                  onClick={() => {
                    if (isDisabled) return;

                    setPendingCancellation({
                      type: "room",
                      roomId: rooms[0].id,
                    });
                  }}
                >
                  <Ban className="h-4 w-4" />
                  Cancel Room
                </Button>

                <Button
                  type="button"
                  variant="destructive"
                  className="h-8 w-full px-3 text-xs sm:w-auto"
                  disabled={isDisabled || rooms.length === 0 || cancellingTour}
                  onClick={() => {
                    if (isDisabled) return;
                    setPendingCancellation({ type: "tour" });
                  }}
                >
                  <Ban className="h-4 w-4" />
                  {isDisabled ? "Tour Cancelled" : "Cancel Tour"}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
        {/* Tour Summary */}
        <Card
          className="border-blue-200 shadow-none"
          style={{ marginTop: "0px", gap: "2px" }}
        >
          <CardHeader style={{ padding: "12px" }}>
            <CardTitle className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-blue-600" />
              Tour Summary
            </CardTitle>
          </CardHeader>
          <CardContent
            className="p-2 pt-2"
            style={{ padding: "0px 12px 12px 12px" }}
          >
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="min-w-0 rounded-md bg-blue-50/60 px-2.5 py-2">
                <Label className="block text-[10px] text-gray-600">
                  Tour Name
                </Label>
                <p
                  className="truncate text-sm font-medium text-gray-900"
                  title={tourName}
                >
                  {tourName}
                </p>
              </div>
              <div className="rounded-md bg-blue-50/60 px-2.5 py-2">
                <Label className="block text-[10px] text-gray-600">
                  Total Applicants
                </Label>
                <p className="text-sm font-medium text-gray-900">
                  {selectedApplicants.length}
                </p>
              </div>
              {isFaizTour && (
                <div className="rounded-md bg-blue-50/60 px-2.5 py-2">
                  <Label className="block text-[10px] text-gray-600">
                    Faiz Accommodation
                  </Label>
                  <p className="text-sm font-medium text-gray-900">
                    {faizAccommodation === "yes" ? "Yes" : "No"}
                  </p>
                </div>
              )}
              {!isMiqaatTour && (
                <div className="rounded-md bg-orange-50 px-2.5 py-2">
                  <Label className="block text-[10px] text-orange-700">
                    Excursions
                  </Label>
                  <p className="text-sm font-semibold text-gray-900">
                    {Object.values(excursionsByDay).flat().length}
                  </p>
                </div>
              )}
              {!isMiqaatTour && (
                <div className="rounded-md bg-pink-50 px-2.5 py-2">
                  <Label className="block text-[10px] text-pink-700">
                    Services
                  </Label>
                  <p className="text-sm font-semibold text-gray-900">
                    {mockServices.length}
                  </p>
                </div>
              )}
              <div className="rounded-md bg-purple-50 px-2.5 py-2">
                <Label className="block text-[10px] text-purple-700">
                  Venues
                </Label>
                <p className="text-sm font-semibold text-gray-900">
                  {venues.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div>
        <Card
          className="border-blue-200 shadow-none"
          style={{ gap: "0px", marginTop: "16px" }}
        >
          <CardHeader
            className="flex flex-row flex-wrap items-center justify-between gap-2"
            style={{ padding: "12px 16px", display: "flex" }}
          >
            <CardTitle className="flex items-center gap-2 text-sm">
              <Receipt className="h-4 w-4 text-blue-600" />
              Booking Receipt
            </CardTitle>
            {canCreateBookingReceipt && (
              <Button
                type="button"
                disabled={!tourPublicId}
                className="h-9 text-xs text-white"
                style={{ backgroundColor: "#5B9BD5" }}
                onClick={() =>
                  navigate("/booking-receipts/create", {
                    state: {
                      bookingReceiptTour: {
                        id: tourPublicId,
                        label: tourName || tourPublicId,
                        publicId: tourPublicId,
                        tourCode,
                        lawazimAmount: bookingReceiptSummary.totalAmount,
                      },
                    },
                  })
                }
              >
                <Plus className="h-4 w-4" />
                Create Receipt
              </Button>
            )}
          </CardHeader>
          <CardContent
            className="space-y-4"
            style={{ padding: "0px 16px 16px" }}
          >
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {[
                {
                  label: "Total Lawazim",
                  amount: bookingReceiptSummary.totalAmount,
                  background: "#DBEAFE",
                  color: "#2563EB",
                },
                {
                  label: "Amount Received",
                  amount: bookingReceiptSummary.amountReceived,
                  background: "#D6F7F3",
                  color: "#16A34A",
                },
                {
                  label: "Balance Due",
                  amount: bookingReceiptSummary.balanceDue,
                  background: "#F7ECE2",
                  color: "#EA580C",
                },
              ].map((summary) => (
                <div
                  key={summary.label}
                  className="rounded-md px-3 py-3"
                  style={{ backgroundColor: summary.background }}
                >
                  <p
                    className="text-xs font-medium"
                    style={{ color: summary.color }}
                  >
                    {summary.label}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    ${formatReceiptAmount(summary.amount)}
                  </p>
                </div>
              ))}
            </div>
            <div className="overflow-hidden rounded-xl border border-sky-200 bg-white">
              <Table className="min-w-[560px] text-xs">
                <TableHeader>
                  <TableRow
                    className="hover:bg-[#E3F2FD]"
                    style={{ backgroundColor: "#E3F2FD" }}
                  >
                    {[
                      "Receipt No",
                      "Received Amount",
                      "Received Date",
                      "Received By",
                      "Actions",
                    ].map((label) => (
                      <TableHead
                        key={label}
                        className="px-4 text-xs text-sky-800"
                      >
                        {label}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookingReceiptsLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="px-4 py-6 text-center text-sm text-slate-500"
                      >
                        Loading booking receipts...
                      </TableCell>
                    </TableRow>
                  ) : bookingReceipts.length > 0 ? (
                    bookingReceipts.map((receipt) => (
                      <TableRow
                        key={receipt.publicId}
                        className="border-slate-200"
                      >
                        <TableCell className="px-4 font-medium text-slate-700">
                          {receipt.receiptNumber}
                        </TableCell>

                        <TableCell className="px-4 text-slate-500">
                          $ {formatReceiptAmount(receipt.amountReceived)}
                        </TableCell>

                        <TableCell className="px-4 text-slate-500">
                          {new Date(receipt.receivedDate).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </TableCell>

                        <TableCell className="px-4 text-slate-500">
                          {receipt.receivedBy}
                        </TableCell>

                        <TableCell className="px-4">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-[#5B9BD5]"
                            aria-label={`Download ${receipt.receiptNumber}`}
                            title="Download receipt"
                            onClick={() =>
                              handleDownloadBookingReceipt(receipt)
                            }
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="px-4 py-6 text-center text-sm text-slate-500"
                      >
                        No booking receipts found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={showCancellationPolicy}
        onOpenChange={setShowCancellationPolicy}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Cancellation Policy</DialogTitle>
            <DialogDescription>
              Review the applicable cancellation tiers.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto rounded-lg border">
            <div className="grid grid-cols-[2fr_2fr_1fr] bg-gray-50 px-4 py-3 text-xs font-semibold text-gray-600">
              <span>Days Before Check-In</span>
              <span>Based On</span>
              <span>Charges</span>
            </div>

            {cancellationTiers.length > 0 ? (
              cancellationTiers.map((tier, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[2fr_2fr_1fr] border-t px-4 py-3 text-sm"
                >
                  <span>{tier.daysBeforeCheckIn} Days</span>
                  <span>{tier.basedOnDisplayName}</span>
                  <span className="font-semibold text-amber-700">
                    {tier.penaltyValue} %
                  </span>
                </div>
              ))
            ) : (
              <div className="p-5 text-center text-gray-500">
                No cancellation policy found.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={supportChatOpen} onOpenChange={setSupportChatOpen}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="border-b bg-sky-50 px-4 py-3 text-left">
            <DialogTitle className="flex items-center gap-2 text-base text-gray-900">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-[#5B9BD5] shadow-sm">
                <MessageSquareText className="h-4 w-4" />
              </span>
              Chat with Admin
            </DialogTitle>
            <DialogDescription className="text-xs">
              Venue and tour support conversation for {tourName}.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[340px] min-h-[220px] space-y-2 overflow-y-auto bg-gray-50 px-4 py-3">
            {supportConversation.map((message) => {
              const isAdmin = message.sender === "admin";
              return (
                <div
                  key={message.id}
                  className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-[82%] rounded-xl border px-3 py-2 shadow-sm ${
                      isAdmin
                        ? "rounded-bl-sm border-blue-200 bg-white"
                        : "rounded-br-sm border-green-200 bg-green-50"
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-semibold text-gray-900">
                          {message.senderName}
                        </span>
                        <Badge
                          className={
                            isAdmin
                              ? "bg-blue-100 px-1.5 py-0 text-[9px] text-blue-700"
                              : "bg-green-100 px-1.5 py-0 text-[9px] text-green-700"
                          }
                        >
                          {isAdmin ? "Admin" : "TO"}
                        </Badge>
                      </div>
                      <span className="text-[9px] text-gray-400">
                        {message.timestamp}
                      </span>
                    </div>
                    <p className="whitespace-pre-line text-xs leading-5 text-gray-700">
                      {message.message}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t bg-white p-3">
            <div className="grid grid-cols-[1fr_auto] items-end gap-2">
              <textarea
                value={supportMessage}
                onChange={(event) => setSupportMessage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    sendSupportMessage();
                  }
                }}
                placeholder="Type message to admin…"
                className="h-10 min-h-10 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#5B9BD5] focus:ring-1 focus:ring-[#5B9BD5]"
              />
              <Button
                type="button"
                size="icon"
                className="h-10 w-10 bg-[#5B9BD5] text-white hover:bg-[#4a8ac2]"
                disabled={!supportMessage.trim()}
                onClick={sendSupportMessage}
                title="Send message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showCancelPaxDialog}
        onOpenChange={setShowCancelPaxDialog}
      >
        <AlertDialogContent className="sm:max-w-xl">
          <AlertDialogHeader>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5" />
                Delete Applicants
              </AlertDialogTitle>
              <AlertDialogDescription>
                This action will cancel the selected applicants from the tour.
                Review the list below before confirming.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogHeader>

          <div className="max-h-[320px] space-y-1 overflow-y-auto rounded-lg border bg-gray-50 p-2">
            {uniqueCancelPaxApplicants.length > 0 ? (
              <div className="space-y-1">
                {uniqueCancelPaxApplicants.map((applicant) => (
                  <div
                    key={applicant.visaApplicationId}
                    className="flex items-center gap-2 rounded-lg border border-white bg-white/90 p-1.5"
                  >
                    <GenderAvatar
                      gender={applicant.gender}
                      name={applicant.applicantName}
                      size="sm"
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-[10px] font-semibold text-slate-900">
                          {applicant.applicantName}
                        </div>
                      </div>

                      <div className="text-[9px] text-slate-400">
                        ITS: {applicant.itsNo}
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-transparent hover:text-red-700"
                      onClick={() => {
                        setApplicantToDelete({
                          id: applicant.visaApplicationId,
                          publicId: applicant.publicId,
                          name: applicant.applicantName,
                          itsNumber: applicant.itsNo,
                          gender: applicant.gender,
                          isShortlisted: applicant.isShortlisted,
                        });

                        setShowPaxDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex min-h-[120px] items-center justify-center">
                <p className="text-sm text-gray-500">No applicants found.</p>
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCancelPaxDialog(false)}
            >
              Keep Booking
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={showPaxDeleteDialog}
        onOpenChange={(open) => {
          setShowPaxDeleteDialog(open);

          if (!open) {
            setApplicantToDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Applicant</DialogTitle>

            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">"{applicantToDelete?.name}"</span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPaxDeleteDialog(false);
                setApplicantToDelete(null);
              }}
            >
              No, Cancel
            </Button>

            <Button
              onClick={cancelPax}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Room</DialogTitle>

            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                "{roomToDelete?.number} - {roomToDelete?.type}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setRoomToDelete(null);
              }}
            >
              No, Cancel
            </Button>

            <Button
              onClick={confirmRoomDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingCancellation !== null}
        onOpenChange={(open) => {
          if (!open) setPendingCancellation(null);
        }}
      >
        <AlertDialogContent className="sm:max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              Reconfirm cancellation
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action will cancel{" "}
              {pendingCancellation?.type === "tour"
                ? "the whole tour and every room"
                : "the selected room"}
              . Review the applicable Lawazim charge before confirming.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {pendingCancellation?.type === "room" && (
            <div className="max-h-[320px] divide-y divide-gray-100 overflow-y-auto rounded-lg border">
              {cancellationPreviews.map((preview) => {
                const cancelled =
                  isTourCancelled ||
                  preview.room.status?.toLowerCase() === "cancelled";

                return (
                  <div
                    key={preview.room.id}
                    className={`p-2.5 ${cancelled ? "bg-gray-50" : "bg-white"}`}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-start gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
                          <BedDouble className="h-4 w-4" />
                        </span>

                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="text-sm font-semibold text-gray-900">
                              Room {preview.room.number} · {preview.room.type}
                            </h4>

                            <Badge variant="outline" className="text-[10px]">
                              {preview.room.venue}
                            </Badge>

                            {cancelled && (
                              <Badge className="bg-red-100 text-[10px] text-red-700">
                                Cancelled
                              </Badge>
                            )}
                          </div>

                          <p className="mt-0.5 text-[11px] text-gray-600">
                            {preview.room.occupancy}/{preview.room.maxOccupancy}{" "}
                            occupied · Base Price:{" "}
                            <span className="font-semibold">
                              {formatAmount(
                                roomCharges.find(
                                  (r) => r.room.id === preview.room.id,
                                )?.charge?.basePrice ?? 0,
                              )}
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center justify-between gap-3 border-t pt-2 sm:border-0 sm:pt-0">
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wide text-gray-500">
                            Charge
                          </p>

                          <p className="text-sm font-bold text-red-700">
                            {formatAmount(
                              roomCharges.find(
                                (r) => r.room.id === preview.room.id,
                              )?.charge?.totalCharge ?? 0,
                            )}
                          </p>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-transparent hover:text-red-700"
                          disabled={cancelled}
                          onClick={() => {
                            setRoomToDelete(preview.room);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            {pendingCancellation?.type === "tour" && (
              <AlertDialogAction
                onClick={confirmCancellation}
                disabled={cancellingTour}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {cancellingTour ? "Cancelling..." : "Yes, Confirm Cancellation"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
